// src/components/TreasuryYields.js
import React, { useEffect, useState } from 'react';

const TreasuryYields = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchYields = async () => {
      setLoading(true);
      try {
        const resp = await fetch('https://metrics-8.onrender.com/api/treasury/yields');
        if (!resp.ok) throw new Error('Failed to fetch treasury data');
        const json = await resp.json();
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchYields();
  }, []);

  if (loading) return <div className="text-sm text-gray-500">Loading treasury data…</div>;
  if (error) return <div className="text-sm text-red-500">{error}</div>;
  if (!data) return null;

  const { current, asOf, curve, inverted, spread_10Y_2Y } = data;

  if (!curve || curve.length === 0) {
    return (
      <div className="card p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-2">US Treasury Yields</h3>
        <p className="text-sm text-gray-500">
          {data.error || 'Treasury yield data not yet available. Set FRED_API_KEY on the server.'}
        </p>
      </div>
    );
  }

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800">US Treasury Yields</h3>
        {asOf && <span className="text-xs text-gray-400">As of {asOf}</span>}
      </div>

      {/* Yield curve visual */}
      <div className="flex items-end gap-2 mb-4 h-32">
        {curve.map((point) => {
          const maxYield = Math.max(...curve.map((p) => p.yield));
          const height = maxYield > 0 ? (point.yield / maxYield) * 100 : 0;
          return (
            <div key={point.maturity} className="flex flex-col items-center flex-1">
              <span className="text-xs font-semibold text-brand-700 mb-1">
                {point.yield.toFixed(2)}%
              </span>
              <div
                className={`w-full rounded-t-md ${inverted ? 'bg-red-400' : 'bg-brand-500'}`}
                style={{ height: `${Math.max(height, 8)}%` }}
              />
              <span className="text-[10px] text-gray-500 mt-1">{point.maturity}</span>
            </div>
          );
        })}
      </div>

      {/* Spread indicator */}
      <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">10Y–2Y Spread:</span>
          <span className={`text-sm font-semibold ${inverted ? 'text-red-600' : 'text-green-600'}`}>
            {spread_10Y_2Y != null ? `${spread_10Y_2Y > 0 ? '+' : ''}${spread_10Y_2Y.toFixed(3)}%` : 'N/A'}
          </span>
        </div>
        {inverted && (
          <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
            ⚠ Inverted
          </span>
        )}
      </div>

      {/* Key rates */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        {['2Y', '10Y', '30Y'].map((key) => (
          <div key={key} className="bg-surface-50 rounded-lg p-3 text-center">
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">{key}</div>
            <div className="text-lg font-bold text-gray-800">
              {current[key] != null ? `${current[key].toFixed(2)}%` : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TreasuryYields;
